import { Label } from '../ui/label';
import { EyeOff, CheckCircle2 } from 'lucide-react';
import { FillTypeBadge } from './FillTypeBadge';
import { FieldInput, ReadOnlyValue } from './FieldInput';
import { effectiveFilledBy, isEmptyValue } from './bookingUtils';

const isWide = (field) => field.type === 'textarea' || (['radio', 'checkbox'].includes(field.type) && (field.options?.length || 0) > 0);

const OfficeField = ({ section, field, officeValues, clientResponses, onChange }) => {
  const type = effectiveFilledBy(section, field);
  const officeEditable = type !== 'CLIENT';
  const clientVal = clientResponses[field.field_id];
  const value = officeValues[field.field_id] ?? clientVal;

  return (
    <div className={isWide(field) ? 'md:col-span-2' : ''} data-testid={`office-field-row-${field.field_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <Label className="text-sm font-semibold text-slate-800">
          {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <div className="flex items-center gap-1.5">
          {field.client_visibility === 'hidden' && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400" title="Hidden from client">
              <EyeOff className="w-3 h-3" /> Hidden
            </span>
          )}
          <FillTypeBadge type={type} compact />
        </div>
      </div>
      {officeEditable ? (
        <FieldInput field={field} value={value} onChange={(v) => onChange(field.field_id, v)} testId={`office-field-${field.field_id}`} />
      ) : (
        <ReadOnlyValue value={clientVal} testId={`client-field-${field.field_id}`} />
      )}
      {!officeEditable && !isEmptyValue(clientVal) && (
        <p className="text-[11px] font-medium text-emerald-700 flex items-center gap-1 mt-1.5">
          <CheckCircle2 className="w-3 h-3" /> Submitted by client
        </p>
      )}
      {field.help_text && <p className="text-xs text-slate-500 mt-1.5">{field.help_text}</p>}
    </div>
  );
};

export const OfficeSectionCard = ({ section, index, officeValues, clientResponses, onChange }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6 relative hover:border-slate-300 transition-colors" data-testid={`office-section-${section.section_id}`}>
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-full bg-[#0F172A] text-[#D4AF37] font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">{index + 1}</span>
        <div>
          <h2 className="font-heading text-xl text-slate-900 leading-tight">{section.title || 'Untitled Section'}</h2>
          {section.description && <p className="text-sm text-slate-500 mt-1">{section.description}</p>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <FillTypeBadge type={section.filled_by || 'CLIENT'} />
        {section.client_visibility === 'hidden' && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400"><EyeOff className="w-3 h-3" /> Hidden from client</span>
        )}
      </div>
    </div>
    {(section.fields || []).length === 0 ? (
      <p className="text-sm text-slate-400 italic">No fields in this section</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {section.fields.map(field => (
          <OfficeField key={field.field_id} section={section} field={field} officeValues={officeValues} clientResponses={clientResponses} onChange={onChange} />
        ))}
      </div>
    )}
  </section>
);
